import React, { useEffect, useRef, useState } from 'react';

const ImageZoom: React.FC<{ src: string; alt?: string;[key: string]: any }> = ({ src, alt, ...rest }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const [animated, setAnimated] = useState(false); // 初始帧需要先设置起始 transform，再在下一帧切到目标
  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (imgRef.current) {
      setOriginRect(imgRef.current.getBoundingClientRect());
    }
    setIsZoomed(true);
    setIsClosing(false);
  };

  const handleCloseZoom = () => {
    setIsClosing(true);
    // 等动画结束后再移除 DOM
    setTimeout(() => {
      setIsZoomed(false);
      setIsClosing(false);
    }, 300);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isZoomed) {
        handleCloseZoom();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isZoomed]);

  useEffect(() => {
    if (isZoomed && !isClosing) {
      setAnimated(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimated(true);
        });
      });
    }
  }, [isZoomed, isClosing]);

  const getImageTransformStyle = (): React.CSSProperties => {
    if (!originRect) return {};

    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const maxW = viewportW * 0.9;
    const maxH = viewportH * 0.9;
    const ratio = Math.min(maxW / originRect.width, maxH / originRect.height);
    const targetW = originRect.width * ratio;
    const targetH = originRect.height * ratio;
    const targetX = (viewportW - targetW) / 2;
    const targetY = (viewportH - targetH) / 2;

    const scaleX = originRect.width / targetW;
    const scaleY = originRect.height / targetH;
    const translateX = originRect.left + originRect.width / 2 - (targetX + targetW / 2);
    const translateY = originRect.top + originRect.height / 2 - (targetY + targetH / 2);

    const isStart = !animated && !isClosing;
    const isEnd = isClosing;

    const shouldShrink = isStart || isEnd;

    return {
      position: 'fixed',
      left: targetX,
      top: targetY,
      width: targetW,
      height: targetH,
      objectFit: 'contain' as const,
      zIndex: 10001,
      transformOrigin: 'center center',
      transform: shouldShrink
        ? `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`
        : 'translate(0, 0) scale(1)',
      transition: shouldShrink && isStart
        ? 'none'
        : 'transform 0.3s cubic-bezier(0.2, 0, 0.2, 1)',
      borderRadius: '8px',
    };
  };

  return (
    <>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="odc-zoomable-image"
        onClick={handleImageClick}
        style={{ cursor: 'zoom-in' }}
        {...rest}
      />
      {isZoomed && (
        <div
          className="odc-image-zoom-overlay"
          onClick={handleCloseZoom}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            backgroundColor: `rgba(10, 10, 10, ${isClosing || !animated ? 0 : 0.36})`,
            transition: 'background-color 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={src}
            alt={alt}
            className="odc-image-zoom-content"
            style={getImageTransformStyle()}
          />
        </div>
      )}
    </>
  );
};

export default ImageZoom;